import { formatTagPairDisplay } from '~/shared/components/filters/search/tagPair';
import {
  countAutoscalingFilterSelections,
  migrateLegacyAutoscalingUrlParams,
  stripAutoscalingFiltersFromApiFilters,
} from '~/shared/utils/autoscalingFilterUtils';
import { safeJsonParse, safeJsonParseArray } from '~/shared/utils/safeJson';
import { SelectOption } from '~/shared/utils/types';

import { bytesInTib } from './constants';
import type { AppliedVolumeFilters } from './panelState';

/**
 * Reads the applied filters back out of the URL. Kept free of React so it runs under vitest's
 * node environment — the same split `filterPanelPlan.ts` and `explorationUrlSyncPlan.ts` use.
 */

const tagFieldRe = /^tag:(.+)$/; // captures tag key after 'tag:'
const valueSplitRe = /\s*,\s*/; // split comma-separated values

/** `field` → the `AppliedVolumeFilters` bucket it fills. Tags are handled separately. */
const fieldToBucket = {
  clusterName: 'appliedClusterNameFilters',
  instanceId: 'appliedInstanceFilters',
  volumeId: 'appliedVolumeIdFilters',
  accountId: 'appliedAccountFilters',
  attachedTo: 'appliedInfrastructureFilters',
  region: 'appliedRegionFilters',
  infrastructure: 'appliedInfrastructureFilters',
  nodeGroupName: 'appliedNodeGroupFilters',
  diskType: 'appliedDiskTypeFilters',
} as const;

type BucketKey = (typeof fieldToBucket)[keyof typeof fieldToBucket];

function normalizeAttachedToValue(value: string): string {
  return value === 'ec2' || value === 'instances' ? 'instance' : value;
}

export interface AppliedFilterUrlSnapshot {
  apiFilters: string | null;
  processedFilters: string | null;
  autoscalingState: string | null;
  autoscalingEligibility: string | null;
}

export interface ParsedAppliedFilters extends AppliedVolumeFilters {
  activeFilterCount: number;
  currentBaseFilters: SelectOption[];
  needsAutoscalingUrlMigration: boolean;
}

function emptyBuckets(): Record<BucketKey | 'appliedVolumeTagFilters', SelectOption[]> {
  return {
    appliedClusterNameFilters: [],
    appliedInstanceFilters: [],
    appliedVolumeIdFilters: [],
    appliedAccountFilters: [],
    appliedRegionFilters: [],
    appliedInfrastructureFilters: [],
    appliedNodeGroupFilters: [],
    appliedDiskTypeFilters: [],
    appliedVolumeTagFilters: [],
  };
}

/** The two capacity groups are one filter to the user, so they count once. */
export function countAppliedBaseFilters(baseFilters: SelectOption[]): number {
  const withoutAutoscaling = stripAutoscalingFiltersFromApiFilters(baseFilters);
  const capacityEnds = withoutAutoscaling.filter(
    (f) => f.group === 'diskSizeMin' || f.group === 'diskSizeMax'
  ).length;

  return withoutAutoscaling.length - capacityEnds + (capacityEnds > 0 ? 1 : 0);
}

function readProcessedFilters(
  processedFilters: string,
  buckets: ReturnType<typeof emptyBuckets>
): void {
  safeJsonParseArray<unknown>(processedFilters).forEach((entry) => {
    const filter =
      typeof entry === 'string'
        ? safeJsonParse<Record<string, unknown> | null>(entry, null)
        : (entry as Record<string, unknown> | null);
    if (!filter) return;

    const { field, value, op } = filter;
    if (typeof field !== 'string' || typeof value !== 'string') return;
    if (op !== 'in' && op !== 'equals' && op !== 'or') return;

    const tagMatch = tagFieldRe.exec(field);

    value.split(valueSplitRe).forEach((raw) => {
      const cleanVal = raw.trim();
      if (!cleanVal) return;

      if (tagMatch) {
        const name = formatTagPairDisplay(tagMatch[1], cleanVal);
        buckets.appliedVolumeTagFilters.push({
          id: name,
          name,
          group: `tag:${tagMatch[1]}` as SelectOption['group'],
        });
        return;
      }

      const bucket = fieldToBucket[field as keyof typeof fieldToBucket];
      if (bucket) {
        const normalizedGroup: SelectOption['group'] =
          field === 'infrastructure' ? 'attachedTo' : (field as SelectOption['group']);
        const normalizedValue =
          normalizedGroup === 'attachedTo' ? normalizeAttachedToValue(cleanVal) : cleanVal;
        buckets[bucket].push({
          id: normalizedValue,
          name: normalizedValue,
          group: normalizedGroup,
        });
      }
    });
  });
}

function readApiFilters(
  currentBaseFilters: SelectOption[],
  buckets: ReturnType<typeof emptyBuckets>
): void {
  (Object.entries(fieldToBucket) as [keyof typeof fieldToBucket, BucketKey][]).forEach(
    ([field, bucket]) => {
      const matches = currentBaseFilters.filter((f) => f.group === field);
      const normalizedMatches =
        field === 'infrastructure'
          ? matches.map((f) => ({
            ...f,
            id: normalizeAttachedToValue(f.id),
            name: normalizeAttachedToValue(f.name),
            group: 'attachedTo' as const,
          }))
          : field === 'attachedTo'
            ? matches.map((f) => ({
              ...f,
              id: normalizeAttachedToValue(f.id),
              name: normalizeAttachedToValue(f.name),
            }))
            : matches;
      buckets[bucket].push(
        // Preserved from All Volumes, which prefixed volume ids on this path only (Reports did
        // not). It makes the same filter render as 'vol-1' via processed_filters and 'id:vol-1'
        // here — almost certainly a display bug, but not one to fix silently while converging.
        ...(field === 'volumeId'
          ? normalizedMatches.map((f) => ({ ...f, name: `id:${f.name}` }))
          : normalizedMatches)
      );
    }
  );
  buckets.appliedVolumeTagFilters.push(
    ...currentBaseFilters.filter((f) => String(f.group).startsWith('tag:'))
  );
}

export function parseAppliedVolumeFilters(
  snapshot: AppliedFilterUrlSnapshot
): ParsedAppliedFilters {
  // Tolerant parse: a hand-edited or truncated `api_filters` must not crash the page.
  const currentBaseFilters = safeJsonParseArray<SelectOption>(snapshot.apiFilters);
  const buckets = emptyBuckets();

  if (snapshot.processedFilters) {
    readProcessedFilters(snapshot.processedFilters, buckets);
  } else if (snapshot.apiFilters) {
    readApiFilters(currentBaseFilters, buckets);
  }

  // Capacity is read from api_filters in BOTH branches: there the two ends are still
  // distinguishable by group, whereas processed_filters has collapsed them onto one
  // `diskSize` field separated only by their operator.
  const capacityTib = (group: 'diskSizeMin' | 'diskSizeMax'): number | undefined => {
    const match = currentBaseFilters.find((f) => f.group === group);
    if (!match) return undefined;
    const bytes = Number(match.id);
    return Number.isFinite(bytes) ? bytes / bytesInTib : undefined;
  };

  const splitIds = (raw: string | null) =>
    (raw || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

  const migrated = migrateLegacyAutoscalingUrlParams(
    splitIds(snapshot.autoscalingState),
    splitIds(snapshot.autoscalingEligibility),
    currentBaseFilters
  );

  return {
    ...buckets,
    appliedCapacityMin: capacityTib('diskSizeMin'),
    appliedCapacityMax: capacityTib('diskSizeMax'),
    appliedAutoscaleState: migrated.stateIds,
    appliedEligibility: migrated.eligibilityIds,
    activeFilterCount:
      countAppliedBaseFilters(currentBaseFilters) +
      countAutoscalingFilterSelections(migrated.stateIds, migrated.eligibilityIds),
    currentBaseFilters,
    needsAutoscalingUrlMigration: migrated.didMigrate,
  };
}
